import React, { useMemo, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import sanitizeHtml from "sanitize-html";
import FeatureModal, { FeatureFormData } from "./FeatureModal";
import DeleteFeatureModal from "./DeleteFeatureModal";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  createFeatureSagaAction,
  deleteFeatureSagaAction,
  getFeatureTechnologyOptionsSagaAction,
  getFeaturesSagaAction,
  updateFeatureSagaAction,
} from "../../../../../../redux/saga/feature-management/saga-actions";
import {
  getCategoriesSagaAction,
  getRequirementsSagaAction,
} from "../../../../../../redux/saga/project-validation/saga-actions";
import {
  CreateFeaturePayloadType,
  FeatureApiItem,
  FeatureSupportApi,
  FeatureTechnologyOption,
  FeatureTechnologySupportApi,
} from "../../../../../../redux/slices/feature-management/Types";
import "./FeatureManagement.scss";

type FeatureItem = {
  id: string;
  name: string;
  description: string;
  usp: "Yes" | "No";
  status: "Active" | "Inactive";
  categoryId: number;
  details: Array<{
    technology: string;
    requirement: string[];
    support: string;
  }>;
  implementationScope: string;
  highLevelScenario: string;
  requirementDescription: string;
};

const resolveTechnologyDisplayName = (
  technology: number | string,
  options: FeatureTechnologyOption[]
): string => {
  const asString = String(technology).trim();
  if (!asString) return "";

  if (/^\d+$/.test(asString)) {
    const byId = options.find((option) => option.id === Number(asString));
    if (byId) return byId.name;
  }

  const byName = options.find((option) => option.name === asString);
  return byName?.name || asString;
};

const buildTechnologyKeySet = (options: FeatureTechnologyOption[]) =>
  new Set(options.flatMap((option) => [String(option.id), option.name]));

const isInactiveTechnology = (
  technology: number | string,
  inactiveKeys: Set<string>
) => inactiveKeys.has(String(technology).trim());

const resolveTechnologyId = (
  technology: string,
  options: FeatureTechnologyOption[]
): number | null => {
  const normalized = technology.trim();
  if (!normalized) return null;

  const byName = options.find((option) => option.name === normalized);
  if (byName) return byName.id;

  if (/^\d+$/.test(normalized)) {
    return Number(normalized);
  }

  return null;
};

const formatDateToDisplay = (value: string) => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const formatSupport = (support: FeatureSupportApi): string => {
  if (support.type === "supported") return "Supported";
  if (support.type === "not_supported") return "Not Supported";
  if (support.config.mode === "fulldate") {
    return formatDateToDisplay(support.config.value);
  }
  return support.config.quarter
    ? `${support.config.quarter} ${String(support.config.value)}`
    : String(support.config.value);
};

const convertDisplayDateToInputDate = (value: string): string => {
  const [day, month, year] = value.split("/");
  if (!year || !month || !day) return "";
  return `${year}-${month}-${day}`;
};

const mapSupportToApi = (value: string): FeatureSupportApi => {
  const normalized = value.trim();
  const lower = normalized.toLowerCase();

  if (lower === "supported") {
    return { type: "supported" };
  }
  if (lower === "not-supported" || lower === "not supported") {
    return { type: "not_supported" };
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
    return {
      type: "custom",
      config: {
        mode: "fulldate",
        value: convertDisplayDateToInputDate(normalized),
      },
    };
  }
  const quarterMatch = normalized.match(/^(Q[1-4])\s+(\d{4})$/i);
  if (quarterMatch) {
    return {
      type: "custom",
      config: {
        mode: "year",
        value: quarterMatch[2],
        quarter: quarterMatch[1].toUpperCase(),
      },
    };
  }
  if (/^\d{4}$/.test(normalized)) {
    return {
      type: "custom",
      config: {
        mode: "year",
        value: normalized,
      },
    };
  }

  // Default fallback for unexpected values from UI.
  return { type: "not_supported" };
};

const mapApiFeatureToUi = (
  feature: FeatureApiItem,
  technologyOptions: FeatureTechnologyOption[],
  inactiveTechnologyKeys: Set<string>
): FeatureItem => ({
  id: String(feature.ID),
  name: feature.feature_name,
  description: "View",
  usp: feature.USP === 1 ? "Yes" : "No",
  status: feature.feature_status,
  categoryId: feature.category_id,
  details: (feature.technology_support || [])
    .filter(
      (detail) => !isInactiveTechnology(detail.technology, inactiveTechnologyKeys)
    )
    .map((detail) => ({
      technology: resolveTechnologyDisplayName(
        detail.technology,
        technologyOptions
      ),
      requirement: detail.requirement || [],
      support: formatSupport(detail.support),
    })),
  implementationScope: feature.implementation_scope,
  highLevelScenario: feature.high_level_scenario || "",
  requirementDescription: feature.requirement_description || "",
});

const FeatureManagement: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const apiFeatures = useAppSelector((state) => state.feature_management.features);
  const isFeaturesLoading = useAppSelector(
    (state) => state.feature_management.status.featuresLoading
  );
  const categoriesData = useAppSelector(
    (state) => state.project_validation.categories
  );
  const requirementsData = useAppSelector(
    (state) => state.project_validation.requirements
  );
  const isCategoriesLoading = useAppSelector(
    (state) => state.project_validation.status.categoriesLoading
  );
  const isRequirementsLoading = useAppSelector(
    (state) => state.project_validation.status.requirementsLoading
  );
  const technologyOptions = useAppSelector(
    (state) => state.feature_management.technologyOptions
  );
  const inactiveTechnologyOptions = useAppSelector(
    (state) => state.feature_management.inactiveTechnologyOptions
  );
  const isTechnologyOptionsLoading = useAppSelector(
    (state) => state.feature_management.status.technologyOptionsLoading
  );
  const isCreateFeatureLoading = useAppSelector(
    (state) => state.feature_management.status.createFeatureLoading
  );
  const isUpdateFeatureLoading = useAppSelector(
    (state) => state.feature_management.status.updateFeatureLoading
  );
  const isDeleteFeatureLoading = useAppSelector(
    (state) => state.feature_management.status.deleteFeatureLoading
  );
  const [toasts, setToasts] = useState<any[]>([]);
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingFeature, setEditingFeature] = useState<FeatureItem | null>(null);
  const [deletingFeature, setDeletingFeature] = useState<FeatureItem | null>(null);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [selectedDescriptions, setSelectedDescriptions] = useState({
    highLevelScenario: "",
    requirementDescription: "",
  });
  const [isActionLoading, setIsActionLoading] = useState(false);
  const catsScrollRef = React.useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const inactiveTechnologyKeys = useMemo(
    () => buildTechnologyKeySet(inactiveTechnologyOptions),
    [inactiveTechnologyOptions]
  );
  const mappedFeatures = useMemo(
    () =>
      apiFeatures.map((feature) =>
        mapApiFeatureToUi(feature, technologyOptions, inactiveTechnologyKeys)
      ),
    [apiFeatures, technologyOptions, inactiveTechnologyKeys]
  );
  const requirementOptions = useMemo(
    () =>
      Array.from(
        new Set(
          requirementsData
            .map((item) => item.requirement_ID)
            .filter((item): item is string => Boolean(item))
        )
      ),
    [requirementsData]
  );
  const isInitialLoading =
    isFeaturesLoading ||
    isCategoriesLoading ||
    isRequirementsLoading ||
    isTechnologyOptionsLoading;
  const isActionBusy =
    isActionLoading ||
    isCreateFeatureLoading ||
    isUpdateFeatureLoading ||
    isDeleteFeatureLoading;

  const categoriesWithCounts = useMemo(() => {
    return categoriesData
      .map((cat) => ({
        id: cat.ID,
        name: cat.category_name,
        featureCount: features.filter((f) => f.categoryId === cat.ID).length,
      }))
      .filter((cat) => cat.featureCount > 0);
  }, [categoriesData, features]);

  const filteredFeatures = useMemo(() => {
    if (selectedCategory === null) return [];
    return features.filter((feature) => feature.categoryId === selectedCategory);
  }, [features, selectedCategory]);

  React.useLayoutEffect(() => {
    const el = catsScrollRef.current;
    if (!el) return;

    const contentWidthEstimate = (container: HTMLElement) => {
      const style = getComputedStyle(container);
      const gap = parseFloat(style.gap || style.columnGap || "0") || 0;
      const nodes = Array.from(container.children) as HTMLElement[];
      if (nodes.length === 0) return 0;
      let sum = 0;
      for (const n of nodes) {
        sum += n.getBoundingClientRect().width;
      }
      return sum + gap * Math.max(0, nodes.length - 1);
    };

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const measured = contentWidthEstimate(el);
      const scrollableWidth = Math.max(scrollWidth, measured);
      const maxScroll = Math.max(0, scrollableWidth - clientWidth);

      const last = el.lastElementChild as HTMLElement | null;
      const cr = el.getBoundingClientRect();
      const rectOverflow =
        last !== null && last.getBoundingClientRect().right > cr.right + 2;

      const hasOverflow = maxScroll > 2 || rectOverflow;

      if (!hasOverflow) {
        setAtStart(true);
        setAtEnd(true);
        return;
      }

      const epsilon = 2;
      setAtStart(scrollLeft <= epsilon);
      setAtEnd(
        maxScroll <= epsilon ? false : scrollLeft >= maxScroll - epsilon
      );
    };

    const scheduleCheck = () => requestAnimationFrame(checkScroll);

    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", scheduleCheck);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleCheck();
          })
        : null;
    ro?.observe(el);
    const parent = el.parentElement;
    if (parent && ro) {
      ro.observe(parent);
    }

    const t0 = window.setTimeout(scheduleCheck, 0);
    const t1 = window.setTimeout(scheduleCheck, 150);
    const t2 = window.setTimeout(scheduleCheck, 400);

    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", scheduleCheck);
      ro?.disconnect();
    };
  }, [categoriesWithCounts]);

  React.useEffect(() => {
    dispatch(getCategoriesSagaAction());
    dispatch(getRequirementsSagaAction());
    dispatch(getFeatureTechnologyOptionsSagaAction());
    dispatch(getFeaturesSagaAction());
  }, [dispatch]);

  React.useEffect(() => {
    setFeatures(mappedFeatures);
  }, [mappedFeatures]);

  React.useEffect(() => {
    if (categoriesWithCounts.length === 0) {
      setSelectedCategory(null);
      return;
    }
    const stillExists = categoriesWithCounts.some(
      (c) => c.id === selectedCategory
    );
    if (!stillExists) {
      setSelectedCategory(categoriesWithCounts[0].id);
    }
  }, [categoriesWithCounts, selectedCategory]);

  const scrollByAmount = (px: number) => {
    const el = catsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: px, behavior: "smooth" });
  };

  const handleToggleRow = (featureId: string) => {
    setExpandedRows((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleOpenAddModal = () => {
    dispatch(getFeatureTechnologyOptionsSagaAction());
    setModalMode("add");
    setEditingFeature(null);
    setIsFeatureModalOpen(true);
  };

  const handleOpenEditModal = (feature: FeatureItem) => {
    dispatch(getFeatureTechnologyOptionsSagaAction());
    setModalMode("edit");
    setEditingFeature(feature);
    setIsFeatureModalOpen(true);
  };

  const pushToast = (variant: "success" | "error", message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant:
          variant === "success"
            ? NotificationVariants.Success
            : NotificationVariants.Error,
        icon: (
          <Iconography
            icon={variant === "success" ? "circleCheck" : "ban"}
            size={IconographySizes.Medium}
          />
        ),
      },
    ]);
  };

  const handleModalSubmit = async (data: FeatureFormData) => {
    const technologySupport = data.details
      .map((item) => {
        const technologyId = resolveTechnologyId(
          item.technology,
          technologyOptions
        );
        if (technologyId == null) {
          return null;
        }
        return {
          technology: technologyId,
          requirement: item.requirement,
          support: mapSupportToApi(item.support),
        };
      })
      .filter(
        (
          item
        ): item is {
          technology: number;
          requirement: string[];
          support: FeatureSupportApi;
        } => item !== null
      );

    const inactiveTechnologySupport: FeatureTechnologySupportApi[] =
      modalMode === "edit" && editingFeature
        ? (
            apiFeatures.find(
              (feature) => String(feature.ID) === editingFeature.id
            )?.technology_support || []
          ).filter((item) =>
            isInactiveTechnology(item.technology, inactiveTechnologyKeys)
          )
        : [];

    const payload: CreateFeaturePayloadType = {
      feature_name: data.name,
      category_id: data.categoryId,
      implementation_scope: data.implementationScope,
      high_level_scenario: data.highLevelScenario,
      requirement_description: data.requirementDescription,
      feature_status: data.status ? "Active" : "Inactive",
      USP: data.usp,
      technology_support: [...technologySupport, ...inactiveTechnologySupport],
    };

    try {
      if (modalMode === "edit" && editingFeature) {
        setIsActionLoading(true);
        dispatch(
          updateFeatureSagaAction({
            id: editingFeature.id,
            data: payload,
            onSuccess: () => {
              pushToast("success", "Feature updated successfully.");
              setIsActionLoading(false);
            },
            onError: (message) => {
              pushToast("error", message || "Unable to update feature.");
              setIsActionLoading(false);
            },
          })
        );
        return;
      }

      setIsActionLoading(true);
      dispatch(
        createFeatureSagaAction({
          data: payload,
          onSuccess: () => {
            pushToast("success", "Feature added successfully.");
            setIsActionLoading(false);
          },
          onError: (message) => {
            pushToast("error", message || "Unable to save feature.");
            setIsActionLoading(false);
          },
        })
      );
      return;
    } catch (error) {
      pushToast("error", "Unable to save feature.");
      setIsActionLoading(false);
    }
  };

  const editingFeaturePayload: FeatureFormData | null = editingFeature
    ? {
        id: editingFeature.id,
        name: editingFeature.name,
        categoryId: editingFeature.categoryId,
        implementationScope: editingFeature.implementationScope,
        usp: editingFeature.usp === "Yes",
        status: editingFeature.status === "Active",
        highLevelScenario: editingFeature.highLevelScenario,
        requirementDescription: editingFeature.requirementDescription,
        details: editingFeature.details,
      }
    : null;

  const handleDeleteConfirm = async () => {
    if (!deletingFeature) return;
    try {
      setIsActionLoading(true);
      dispatch(
        deleteFeatureSagaAction({
          id: deletingFeature.id,
          onSuccess: () => {
            setDeletingFeature(null);
            pushToast("success", "Feature deleted successfully.");
            setIsActionLoading(false);
          },
          onError: (message) => {
            pushToast("error", message || "Unable to delete feature.");
            setIsActionLoading(false);
          },
        })
      );
      return;
    } catch (error) {
      pushToast("error", "Unable to delete feature.");
      setIsActionLoading(false);
    } finally {
      // no-op: state reset handled in callbacks for saga flow
    }
  };

  const handleOpenDescriptionModal = (feature: FeatureItem) => {
    setSelectedDescriptions({
      highLevelScenario: feature.highLevelScenario || "",
      requirementDescription: feature.requirementDescription || "",
    });
    setIsDescriptionModalOpen(true);
  };

  const openFeatureComparisonPage = () => {
    window.open("/resources/feature-comparison-table", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="feature-management-content">
      <Notification toasts={toasts} setToasts={setToasts} />
      {isInitialLoading || isActionBusy ? (
        <div className="feature-loader-wrap">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <>
          <div className="fm-top-row">
            <Typography variant="h6" className="fm-title">
              Categories
            </Typography>
            <div className="fm-actions">
              <Button
                variant="flat"
                label="Feature Comparison"
                onClick={openFeatureComparisonPage}
              />
              <Button
                variant="primary"
                label="Add Feature"
                icon={<Iconography icon="plus" />}
                onClick={handleOpenAddModal}
                disabled={readOnly || isInitialLoading || isActionBusy}
              />
            </div>
          </div>

          <section className="gdl-categories-row" aria-label="Feature categories">
            <Button
              appearance="outlined"
              className="gdl-cat-scroll-btn gdl-cat-scroll-btn--prev"
              onClick={() => scrollByAmount(-320)}
              disabled={atStart || isActionBusy}
              aria-label="Scroll categories left"
              label={<Iconography icon="angleLeft" size={IconographySizes.Small} />}
            />

            <div ref={catsScrollRef} className="gdl-cat-scroll" role="listbox">
              {categoriesWithCounts.map((category) => (
                <button
                  key={category.id}
                  className={`gdl-cat-pill ${
                    selectedCategory === category.id ? "gdl-cat-pill--selected" : ""
                  }`}
                  onClick={() => setSelectedCategory(category.id)}
                  disabled={isInitialLoading || isActionBusy}
                  aria-pressed={selectedCategory === category.id}
                >
                  <div className="gdl-cat__content">
                    <span className="gdl-cat-pill__title">{category.name}</span>
                    <span className="gdl-cat-pill__count">
                      {category.featureCount}{" "}
                      {category.featureCount === 1 ? "Feature" : "Features"}
                    </span>
                  </div>

                  {selectedCategory === category.id ? (
                    <Iconography
                      icon="circleCheck"
                      size={IconographySizes.Small}
                      className="gdl-cat-pill__icon"
                    />
                  ) : (
                    <div className="gdl-cat-pill__radio" />
                  )}
                </button>
              ))}
            </div>

            <Button
              appearance="outlined"
              disabled={atEnd || isActionBusy}
              className="gdl-cat-scroll-btn gdl-cat-scroll-btn--next"
              onClick={() => scrollByAmount(320)}
              aria-label="Scroll categories right"
              label={<Iconography icon="angleRight" size={IconographySizes.Small} />}
            />
          </section>

          <div className="features-section">
            <Typography variant="h6" className="section-title">
              Features Management
            </Typography>
            <Typography className="section-subtitle">
              Configure Feature and USP Requirement
            </Typography>

            <TableContainer component={Paper} className="features-table">
              <Table className="features-main-table">
                <colgroup>
                  <col className="col-feature-name" />
                  <col className="col-description" />
                  <col className="col-usp" />
                  <col className="col-status" />
                  <col className="col-actions" />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className="feature-col-feature">Feature Name</TableCell>
                    <TableCell className="feature-col-desc">Description</TableCell>
                    <TableCell className="feature-col-usp">USP</TableCell>
                    <TableCell className="feature-col-status">Status</TableCell>
                    <TableCell className="feature-actions-col">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeatures.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="feature-empty-cell">
                        No features available.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredFeatures.map((feature) => {
                    const isExpanded = expandedRows.includes(feature.id);

                    return (
                      <React.Fragment key={feature.id}>
                        <TableRow>
                          <TableCell className="feature-col-feature">
                            <div className="feature-name-cell">
                              <Button
                                variant="flat"
                                className="feature-expand-btn"
                                onClick={() => handleToggleRow(feature.id)}
                                disabled={isActionBusy}
                                icon={
                                  <Iconography
                                    icon={isExpanded ? "angleUp" : "angleDown"}
                                    size={IconographySizes.Small}
                                  />
                                }
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${feature.name}`}
                              />
                              <span>{feature.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="feature-col-desc">
                            <button
                              type="button"
                              className="view-link"
                              onClick={() => handleOpenDescriptionModal(feature)}
                            >
                              {feature.description}
                            </button>
                          </TableCell>
                          <TableCell className="feature-col-usp">{feature.usp}</TableCell>
                          <TableCell className="feature-col-status">
                            <Chip
                              label={feature.status}
                              className={`status-chip ${feature.status.toLowerCase()}`}
                            />
                          </TableCell>
                          <TableCell className="feature-actions-col">
                            <div className="feature-actions-cell">
                              <Button
                                variant="flat"
                                className={
                                  readOnly ? "feature-action-btn--disabled" : ""
                                }
                                icon={<Iconography icon="pen" />}
                                onClick={() => {
                                  if (!readOnly) handleOpenEditModal(feature);
                                }}
                                disabled={readOnly || isActionBusy}
                              />
                              <Button
                                variant="flat"
                                className={
                                  readOnly ? "feature-action-btn--disabled" : ""
                                }
                                icon={<Iconography icon="trashCan" />}
                                onClick={() => {
                                  if (!readOnly) setDeletingFeature(feature);
                                }}
                                disabled={readOnly || isActionBusy}
                              />
                            </div>
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow className="expanded-details-row">
                            <TableCell colSpan={5} className="expanded-details-cell">
                              <div className="feature-details-card">
                                <Table size="small" className="feature-details-table">
                                  <colgroup>
                                    <col className="detail-col-1" />
                                    <col className="detail-col-2" />
                                    <col className="detail-col-3" />
                                    <col className="detail-col-4" />
                                    <col className="detail-col-5" />
                                  </colgroup>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell className="detail-th-tech">Technology</TableCell>
                                      <TableCell className="detail-th-req">Requirement</TableCell>
                                      <TableCell
                                        className="detail-th-spacer detail-th-spacer--usp"
                                        aria-hidden
                                      />
                                      <TableCell className="detail-th-support">Support</TableCell>
                                      <TableCell
                                        className="detail-th-spacer detail-th-spacer--actions"
                                        aria-hidden
                                      />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {feature.details.map((detail, index) => (
                                      <TableRow key={`${feature.id}-${index}`}>
                                        <TableCell className="detail-td-tech">{detail.technology}</TableCell>
                                        <TableCell className="detail-td-req">
                                          <div className="requirement-tags">
                                            {detail.requirement.map((req) => (
                                              <span key={req} className="requirement-chip">
                                                {req}
                                              </span>
                                            ))}
                                          </div>
                                        </TableCell>
                                        <TableCell
                                          className="detail-td-spacer detail-td-spacer--usp"
                                          aria-hidden
                                        />
                                        <TableCell className="detail-td-support">
                                          <Chip
                                            label={detail.support}
                                            className={`detail-status-chip ${
                                              detail.support.toLowerCase() === "supported"
                                                ? "supported"
                                                : detail.support.toLowerCase() === "not supported"
                                                ? "not-supported"
                                                : "date-status"
                                            }`}
                                          />
                                        </TableCell>
                                        <TableCell
                                          className="detail-td-spacer detail-td-spacer--actions"
                                          aria-hidden
                                        />
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      )}

      <FeatureModal
        open={isFeatureModalOpen}
        mode={modalMode}
        categories={categoriesData.map((item) => ({
          id: item.ID,
          name: item.category_name,
        }))}
        technologyOptions={technologyOptions}
        requirementOptions={requirementOptions}
        initialData={editingFeaturePayload}
        onClose={() => {
          setIsFeatureModalOpen(false);
          setEditingFeature(null);
        }}
        onSubmit={handleModalSubmit}
      />

      <DeleteFeatureModal
        open={Boolean(deletingFeature)}
        featureName={deletingFeature?.name || ""}
        onClose={() => setDeletingFeature(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Dialog
        open={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.18)",
            maxWidth: 760,
          },
        }}
      >
        <div className="benefits-ptv-modal">
          <header className="benefits-ptv-modal-header">
            <div>
              <p className="benefits-ptv-modal-eyebrow">Validation Management</p>
              <h3>View Description</h3>
              <p className="benefits-ptv-modal-subtitle">
                Review feature description
              </p>
            </div>
            <button
              type="button"
              className="benefits-ptv-modal-close"
              onClick={() => setIsDescriptionModalOpen(false)}
              aria-label="Close"
            >
              <Iconography icon="xmark" size={IconographySizes.Medium} />
            </button>
          </header>
          <DialogContent className="benefits-ptv-modal-content">
            <p className="benefits-description-modal-title">High Level Scenario</p>
            <div
              className="benefits-description-preview"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeHtml(selectedDescriptions.highLevelScenario || "", {
                    allowedTags: [
                      "p",
                      "br",
                      "ul",
                      "ol",
                      "li",
                      "strong",
                      "b",
                      "em",
                      "i",
                      "u",
                      "s",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "h6",
                      "a",
                    ],
                    allowedAttributes: { a: ["href", "target", "rel"] },
                    allowedSchemes: ["http", "https", "mailto", "tel"],
                  }).trim() || "<p>No description added.</p>",
              }}
            />
            <p className="benefits-description-modal-title">Requirement Description</p>
            <div
              className="benefits-description-preview"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeHtml(selectedDescriptions.requirementDescription || "", {
                    allowedTags: [
                      "p",
                      "br",
                      "ul",
                      "ol",
                      "li",
                      "strong",
                      "b",
                      "em",
                      "i",
                      "u",
                      "s",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "h6",
                      "a",
                    ],
                    allowedAttributes: { a: ["href", "target", "rel"] },
                    allowedSchemes: ["http", "https", "mailto", "tel"],
                  }).trim() || "<p>No description added.</p>",
              }}
            />
          </DialogContent>
          <footer className="benefits-ptv-modal-footer">
            <Button
              variant="secondary"
              label="CLOSE"
              onClick={() => setIsDescriptionModalOpen(false)}
            />
          </footer>
        </div>
      </Dialog>
    </div>
  );
};

export default FeatureManagement;
